import { update } from 'lodash';
import api from './api';
import { unstable_batchedUpdates } from 'react-dom';

const studyService = {
  getStudies: async () => {
    try {
      console.debug('[STUDY_SERVICE] Fetching studies...');
      const response = await api.get('/api/studies/');
      console.debug('[STUDY_SERVICE] Raw API response:', response);
      console.debug('[STUDY_SERVICE] Response data:', response.data);
      
      // Add more detailed logging to debug the response structure
      if (response.data) {
        console.debug('[STUDY_SERVICE] Response data type:', typeof response.data);
        console.debug('[STUDY_SERVICE] Is array:', Array.isArray(response.data));
        
        // Check if it's an object with a studies property
        if (typeof response.data === 'object' && !Array.isArray(response.data)) {
          //console.log('[STUDY_SERVICE] Object keys:', Object.keys(response.data));
          //console.log('[STUDY_SERVICE] Has studies property:', 'studies' in response.data);
          //console.log('[STUDY_SERVICE] Has results property:', 'results' in response.data);
          
          if ('studies' in response.data) {
            console.debug('[STUDY_SERVICE] Studies array length:', response.data.studies.length);
          }
          
          if ('results' in response.data) {
            console.debug('[STUDY_SERVICE] Results array length:', response.data.results.length);
            if (response.data.results.length > 0) {
              console.debug('[STUDY_SERVICE] First result keys:', Object.keys(response.data.results[0]));
            }
          }
        }
        
        // If it's an array, check its structure
        if (Array.isArray(response.data)) {
          console.debug('[STUDY_SERVICE] Array length:', response.data.length);
          if (response.data.length > 0) {
            console.debug('[STUDY_SERVICE] First item keys:', Object.keys(response.data[0]));
          }
        }
      }
      
      // Return the results array if it exists, otherwise return the data as is
      return response.data.results || response.data;
    } catch (error) {
      console.error('[STUDY_SERVICE] Error fetching studies:', error);
      throw error;
    }
  },

  getSingleStudy: async (studyID) => {
    const response = await api.get(`/api/studies/${studyID}/edit/`);
    // Backend returns { status: 'success', study: {...} }
    return response.data.study || response.data;
  },

  createStudy: async (studyData) => {
    const response = await api.post('/api/admin/add-study/', studyData);
    return response.data;
  },

  updateStudy: async (studyID, studyData) => {
    const response = await api.put(`/api/studies/${studyID}/edit/`, studyData);
    return response.data;
  },

  getStudyMinMaxParticipantId: async (studyID) => {
    const response = await api.get(`/api/studies/${studyID}/`);
    const study = response.data;
    return [ study.min_participant_id, study.max_participant_id ]
  },
  
  getStudyLanguages: async (studyID) => {
    try {
      // Use the new API endpoint for languages
      const response = await api.get(`/api/studies/${studyID}/languages/`);
      // If the endpoint returns formatted data, use it directly
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }
      
      // Fallback to the original implementation if the endpoint doesn't work
      const studyResponse = await api.get(`/api/studies/${studyID}/edit/`);
      const study = studyResponse.data;
      
      // Extract languages from study data or draft
      let languages = [];
      
      // Try to get languages from study data
      if (study.data && study.data.languages) {
        languages = study.data.languages;
      } 
      // If not found, try to get from latest draft
      else if (study.drafts && study.drafts.length > 0) {
        const latestDraft = study.drafts[0];
        if (latestDraft.data && latestDraft.data.languages) {
          languages = latestDraft.data.languages;
        }
      }
      
      // Ensure English is always included and at the top
      if (!languages.includes('en')) {
        languages.unshift('en');
      } else {
        // Move English to the top if it exists
        const index = languages.indexOf('en');
        languages.splice(index, 1);
        languages.unshift('en');
      }
      
      // Map language codes to readable names
      const languageMap = {
        'en': 'English',
        'es': 'Spanish',
        'fr': 'French',
        'de': 'German',
        'it': 'Italian',
        'pt': 'Portuguese',
        'ru': 'Russian',
        'zh': 'Chinese',
        'ja': 'Japanese',
        'ko': 'Korean'
        // Add more languages as needed
      };
      
      return languages.map(code => ({
        code,
        name: languageMap[code] || code
      }));
    } catch (error) {
      console.error('Error fetching study languages:', error);
      // Return English as fallback
      return [{ code: 'en', name: 'English' }];
    }
  }
};

export default studyService;
